"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import type { ChatMessage } from "../MessageBubble";
import { advanceReveal } from "./streamPacing";
import type { MessageHistory } from "./useMessagePoll";
import type { MessageThread } from "./useMessageThread";

/**
 * Sends what the reader typed and streams the reply back: the POST reads
 * `res.body` directly, buffering everything that arrives, while a paced
 * reveal loop decides what reaches the screen and when. Once the reply is
 * fully shown the thread is refetched so the server-persisted copy replaces
 * the streamed one.
 */
export function useStreamedReply({
  workerId,
  businessId,
  thread,
  history,
}: {
  /** null = Smithers' global cross-worker thread. */
  workerId: string | null;
  businessId: string;
  thread: MessageThread;
  history: MessageHistory;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    mountedRef,
    sendingRef,
    stickToBottomRef,
    msgsRef,
    setMsgs,
    setHasUnread,
    setUnreadFromId,
    commitMessages,
  } = thread;
  const { refreshMessages, syncRef, olderRef, setLoadingOlder, setWorking } = history;
  const [streamingText, setStreamingText] = useState<string | null>(null);
  /** Fixed when the stream opens — Date.now() during render is impure. */
  const [streamStartedAt, setStreamStartedAt] = useState(0);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  /**
   * Send failures are separate from persisted messages: the route can reject
   * before writing anything, and later syncs should not erase the feedback.
   */
  const [sendError, setSendError] = useState<string | null>(null);
  /** Everything received from the network so far, revealed or not. */
  const bufferRef = useRef("");
  /** When the first byte landed, for measuring how fast text is arriving. */
  const streamStartRef = useRef(0);
  /** How much of the buffer is on screen. */
  const revealedRef = useRef(0);
  /** True once the network stream has closed, so the reveal can finish. */
  const streamClosedRef = useRef(false);
  /** Aborts the in-flight reply when the reader presses Stop. */
  const abortRef = useRef<AbortController | null>(null);
  /** Cancels whichever timer currently drives the reveal. */
  const cancelRef = useRef<(() => void) | null>(null);
  /** Resolves when the last buffered character has been revealed. */
  const drainedRef = useRef<(() => void) | null>(null);

  /**
   * Paces buffered stream text onto the screen and resolves once everything
   * received has been shown.
   *
   * Frames are scheduled through requestAnimationFrame while the tab is
   * visible and setTimeout while it is not — browsers stop firing rAF
   * entirely in a hidden tab, which would stall the reveal, and with it the
   * awaited drain and the refetch that finalises the message. Hidden also
   * means nobody is watching an animation, so it catches up in one go.
   *
   * Readers who ask for reduced motion get the text immediately: a
   * character-by-character reveal is an animation, and the point of the
   * setting is not to have to watch one.
   */
  const revealBuffered = useCallback((): Promise<void> => {
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    return new Promise<void>((resolve) => {
      if (reduceMotion) {
        revealedRef.current = bufferRef.current.length;
        setStreamingText(bufferRef.current);
        resolve();
        return;
      }

      drainedRef.current = resolve;
      let last = performance.now();

      const finish = () => {
        cancelRef.current = null;
        drainedRef.current = null;
        resolve();
      };

      const schedule = (fn: () => void) => {
        if (typeof document !== "undefined" && document.hidden) {
          const id = setTimeout(fn, 200);
          cancelRef.current = () => clearTimeout(id);
        } else {
          const id = requestAnimationFrame(fn);
          cancelRef.current = () => cancelAnimationFrame(id);
        }
      };

      const step = () => {
        const now = performance.now();
        const dt = Math.min(now - last, 100);
        last = now;

        const hidden = typeof document !== "undefined" && document.hidden;
        const backlog = bufferRef.current.length - revealedRef.current;

        if (backlog > 0) {
          if (hidden) {
            revealedRef.current = bufferRef.current.length;
          } else {
            revealedRef.current = advanceReveal({
              buffer: bufferRef.current,
              revealed: revealedRef.current,
              dt,
              elapsedMs: now - streamStartRef.current,
              streamClosed: streamClosedRef.current,
            });
          }
          setStreamingText(bufferRef.current.slice(0, revealedRef.current));
        }

        // Only stop once the network is done AND the buffer is drained,
        // otherwise the tail of the reply would never be painted.
        if (
          streamClosedRef.current &&
          revealedRef.current >= bufferRef.current.length
        ) {
          finish();
          return;
        }
        schedule(step);
      };

      schedule(step);
    });
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      cancelRef.current?.();
      drainedRef.current?.();
    };
  }, []);

  async function send() {
    const text = input.trim();
    if (!text || sendingRef.current) return;
    // A response that began before this optimistic send must never replace it.
    syncRef.current?.abort();
    syncRef.current = null;
    olderRef.current?.abort();
    olderRef.current = null;
    setLoadingOlder(false);

    setSending(true);
    sendingRef.current = true;
    // Sending is an explicit "I'm done reading history" signal.
    stickToBottomRef.current = true;
    setSendError(null);
    setHasUnread(false);
    setUnreadFromId(null);
    setInput("");
    const optimistic: ChatMessage[] = [
      ...msgsRef.current,
      {
        id: `local-${Date.now()}`,
        role: "user",
        content: text,
        source: "chat",
        createdAt: Date.now(),
      },
    ];
    msgsRef.current = optimistic;
    setMsgs(optimistic);

    let streamStarted = false;
    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(workerId ? "/api/chat" : "/api/manager/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          workerId
            ? { workerId, message: text }
            : { businessId, message: text, page: pathname }
        ),
        signal: controller.signal,
      });

      if (!mountedRef.current) return;
      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => null);
        setSendError(data?.error ?? res.statusText ?? "The request failed.");
        // Nothing was sent — hand the text back so it isn't lost.
        setInput(text);
        return;
      }

      streamStarted = true;
      if (!stickToBottomRef.current) setHasUnread(true);
      setStreamStartedAt(Date.now());
      setStreamingText("");

      bufferRef.current = "";
      revealedRef.current = 0;
      streamClosedRef.current = false;
      streamStartRef.current = performance.now();
      const drained = revealBuffered();

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          // Only buffered here — the reveal loop decides what reaches the
          // screen and when, so a burst does not land as one lump.
          bufferRef.current += decoder.decode(value, { stream: true });
        }
      } finally {
        // Set even when the read throws, or the reveal loop would spin
        // forever waiting for a stream that has already failed.
        streamClosedRef.current = true;
      }
      // Let the tail finish typing before the finally block swaps in the
      // server-persisted copy; otherwise the last words are cut off.
      await drained;
    } catch (err) {
      // Stopping is a deliberate act, not a failure — no error to report, and
      // whatever streamed before the stop stays on screen.
      if (mountedRef.current && !(err instanceof DOMException && err.name === "AbortError")) {
        setSendError(err instanceof Error ? err.message : String(err));
        // Mid-stream failures already persisted the user message server-side;
        // only hand the text back when the send never landed.
        if (!streamStarted) setInput(text);
      }
    } finally {
      abortRef.current = null;
      if (mountedRef.current) {
        // Sync only persisted deltas; a failed send still removes its local echo.
        if (!streamStarted) commitMessages(msgsRef.current.filter(message => !message.id.startsWith("local-")), false);
        await refreshMessages(true);
        if (mountedRef.current) {
          window.dispatchEvent(new CustomEvent("caw:activity-refresh", { detail: workerId ? { workerId } : {} }));
          router.refresh();
          setStreamingText(null);
          // This tab's own turn just ended; a poll from before it must not keep the indicator up.
          // A refused send (another tab's turn is running) keeps what the server just said.
          if (streamStarted) setWorking(false);
          setSending(false);
          sendingRef.current = false;
        }
      }
      cancelRef.current?.();
      cancelRef.current = null;
      drainedRef.current?.();
      drainedRef.current = null;
    }
  }

  /**
   * Cuts the reply short, leaving on screen exactly what had been shown.
   *
   * Three things have to stop, not one. Aborting the fetch is often a no-op
   * on its own — a fast model finishes well before the reveal has typed out
   * its buffer, so by the time anyone reaches for Stop the request is
   * already complete. The buffer is then truncated to what was revealed, so
   * the rest is never typed out: a reader stopping a wrong answer does not
   * want the remainder dumped on them. And the abort closes the connection,
   * which the route now treats as a signal to stop generating and to save
   * only what was actually said.
   */
  function stop() {
    // Closing the connection no longer ends Smithers' turn on the server; asking does.
    if (!workerId) void fetch("/api/manager/chat/stop", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ businessId }), keepalive: true,
    }).catch(() => {});
    abortRef.current?.abort();
    streamClosedRef.current = true;
    bufferRef.current = bufferRef.current.slice(0, revealedRef.current);
  }

  return {
    input,
    setInput,
    sending,
    sendError,
    setSendError,
    streamingText,
    streamStartedAt,
    send,
    stop,
  };
}
